import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLabels } from "@/lib/auth";
import { ChevronRight, ChevronDown, Search, MapPin, FlaskConical, Box } from "lucide-react";

/** Find Stock — search any name (ours, supplier's, alias, SKU) to see where an
 * item sits; or browse the godown tree. Answers both "where is it?" and
 * "where should I put this back?". */
export default function StockBrowser() {
  const L = useLabels();
  const { data, isLoading } = useQuery({
    queryKey: ["stock-browse"],
    queryFn: async () => (await api.get("/stock/browse")).data,
  });
  const { data: totals = [] } = useQuery({
    queryKey: ["stock-current"],
    queryFn: async () => (await api.get("/stock/current")).data,
  });
  const { data: mappedRooms = [] } = useQuery({
    queryKey: ["map-rooms"],
    queryFn: async () => (await api.get("/map/rooms")).data,
  });
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");

  // which 3D room (if any) a location belongs to — walks up the tree
  const mappedRoomFor = (locId) => {
    if (!data) return null;
    const byId = Object.fromEntries(data.map(l => [l.location_id, l]));
    const rooms = new Set(mappedRooms.map(r => r.id));
    let cur = byId[locId]; const seen = new Set();
    while (cur && !seen.has(cur.location_id)) {
      if (rooms.has(cur.location_id)) return cur.location_id;
      seen.add(cur.location_id); cur = byId[cur.parent_id];
    }
    return null;
  };
  const mapLink = (locId, query = "") => {
    const room = mappedRoomFor(locId);
    if (!room) return null;
    const p = new URLSearchParams({ room, shelf: locId, q: query });
    return `/map?${p.toString()}`;
  };

  const locPath = useMemo(() => {
    if (!data) return {};
    const byId = Object.fromEntries(data.map(l => [l.location_id, l]));
    const path = {};
    for (const l of data) {
      const parts = [];
      let cur = l;
      const seen = new Set();
      while (cur && !seen.has(cur.location_id)) {
        parts.unshift(cur.location_name);
        seen.add(cur.location_id);
        cur = byId[cur.parent_id];
      }
      path[l.location_id] = parts.join(" › ");
    }
    return path;
  }, [data]);

  if (isLoading || !data) return <div className="text-muted-foreground">Loading stock…</div>;

  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  const query = q.trim().toLowerCase();

  // item-centric search results: item -> [{location, qty, containers}]
  let results = [];
  if (query) {
    const byItem = {};
    for (const loc of data) {
      for (const it of loc.direct_items) {
        const names = [it.item_name, it.sku, it.supplier_name, ...(it.aliases || [])]
          .filter(Boolean).map(s => s.toLowerCase());
        if (!names.some(n => n.includes(query))) continue;
        const e = byItem[it.item_id] ||= { ...it, locations: [] };
        e.locations.push({
          location_id: loc.location_id,
          path: locPath[loc.location_id] || loc.location_name,
          qty: it.total,
          containers: it.batches.flatMap(b => b.containers),
        });
      }
    }
    // items matching by name but with zero stock anywhere (e.g. pure blends)
    for (const t of totals) {
      const names = [t.name, t.sku, t.supplier_name].filter(Boolean).map(s => s.toLowerCase());
      if (!names.some(n => n.includes(query))) continue;
      if (!byItem[t.item_id]) {
        byItem[t.item_id] = {
          item_id: t.item_id, item_name: t.name, sku: t.sku,
          supplier_name: t.supplier_name, aliases: [], base_unit: t.base_unit,
          locations: [],
        };
      }
    }
    const totalsById = Object.fromEntries(totals.map(t => [t.item_id, t]));
    results = Object.values(byItem).map(e => ({
      ...e,
      total: totalsById[e.item_id]?.total_qty ?? e.locations.reduce((s, l) => s + l.qty, 0),
      blendable: totalsById[e.item_id]?.blendable_qty || 0,
    }));
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stock-browser">
      <div>
        <div className="data-label">Where things are</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Find Stock</h1>
        <p className="text-sm text-muted-foreground">
          Search by any name — ours, an {L.tradeNameLower}, or an alias. Results show every spot
          holding the item, so you also know where to put a container back.
        </p>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          className="field pl-9"
          placeholder="Search item… e.g. Rose Supreme, Galaxolide, Pudina"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="stock-search"
        />
      </div>

      {query ? (
        <div className="space-y-3" data-testid="stock-search-results">
          {results.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground bg-white border border-border rounded-sm">
              Nothing matches “{q}”.
            </div>
          )}
          {results.map(r => (
            <div key={r.item_id} className="bg-white border border-border rounded-sm p-4"
                 data-testid={`stock-result-${r.sku}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{r.item_name}</div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-mono">{r.sku}</span>
                    {r.supplier_name && <> · {L.tradeNameLower}: {r.supplier_name}</>}
                    {r.aliases?.length > 0 && <> · aka {r.aliases.join(", ")}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold">
                    {r.total.toLocaleString()} <span className="text-sm text-muted-foreground">{r.base_unit}</span>
                  </div>
                  {r.blendable > 0 && (
                    <div className="text-xs text-secondary flex items-center gap-1 justify-end">
                      <FlaskConical className="w-3 h-3" />
                      + {r.blendable.toLocaleString()} {r.base_unit} can be blended
                    </div>
                  )}
                </div>
              </div>
              {r.locations.length === 0 ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  No ready stock on any rack{r.blendable > 0 ? " — blend it from components (see Formulations)" : ""}.
                </div>
              ) : (
                <ul className="mt-3 space-y-1">
                  {r.locations.map(l => {
                    const href = mapLink(l.location_id, r.item_name);
                    return (
                      <li key={l.location_id} className="flex items-center gap-2 text-sm flex-wrap">
                        <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="truncate">{l.path}</span>
                        <span className="text-xs text-muted-foreground">
                          · {l.containers.length} container{l.containers.length === 1 ? "" : "s"}
                        </span>
                        <span className="ml-auto font-mono font-semibold">{l.qty.toLocaleString()} {r.base_unit}</span>
                        {href && (
                          <Link to={href} className="btn-primary text-xs" data-testid={`map-link-${r.sku}`}>
                            <Box className="w-3.5 h-3.5 mr-1" /> Show on map
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      ) : (
      <div className="bg-white border border-border rounded-sm divide-y divide-border">
        {data.filter(l => l.direct_items.length).map((loc) => {
          const key = `loc-${loc.location_id}`;
          return (
            <div key={loc.location_id} className="animate-fade-in">
              <button
                onClick={() => toggle(key)}
                className="w-full flex items-center gap-3 px-4 py-3 row-hover text-left"
                data-testid={`loc-toggle-${loc.location_code}`}
              >
                {expanded[key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="badge-flat bg-primary/10 text-primary">{loc.location_type}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{locPath[loc.location_id] || loc.location_name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{loc.location_code}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs data-label">{loc.direct_items.length} items</div>
                </div>
              </button>
              {mapLink(loc.location_id) && (
                <div className="px-4 pb-2 -mt-1">
                  <Link to={mapLink(loc.location_id)} className="text-xs font-semibold uppercase tracking-widest text-primary">
                    <Box className="w-3.5 h-3.5 inline mr-1" />Show on map
                  </Link>
                </div>
              )}
              {expanded[key] && (
                <div className="pl-6 pr-4 pb-3 space-y-2 border-l-2 border-primary/10 ml-4">
                  {loc.direct_items.map((it) => {
                    const ikey = `${key}-${it.item_id}`;
                    return (
                      <div key={it.item_id} className="border border-border rounded-sm">
                        <button
                          onClick={() => toggle(ikey)}
                          className="w-full flex items-center gap-3 px-3 py-2 row-hover text-left"
                          data-testid={`item-toggle-${it.sku}`}
                        >
                          {expanded[ikey] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{it.item_name}</div>
                            <div className="font-mono text-xs text-muted-foreground">
                              {it.sku}{it.supplier_name ? ` · ${it.supplier_name}` : ""}
                            </div>
                          </div>
                          <div className="font-mono text-sm font-semibold">{it.total} <span className="text-muted-foreground">{it.base_unit}</span></div>
                        </button>
                        {expanded[ikey] && (
                          <div className="pl-6 pr-3 pb-3 space-y-2">
                            {it.batches.map((b) => {
                              const bkey = `${ikey}-${b.batch_id}`;
                              return (
                                <div key={b.batch_id} className="border-l-2 border-secondary/40 pl-3">
                                  <button
                                    onClick={() => toggle(bkey)}
                                    className="w-full flex items-center gap-2 py-1 text-left"
                                    data-testid={`batch-toggle-${b.batch_number}`}
                                  >
                                    {expanded[bkey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    <span className="container-code">{b.batch_number}</span>
                                    <span className="text-xs text-muted-foreground">· {b.supplier_name}</span>
                                    <span className="ml-auto font-mono text-xs">{b.total} {it.base_unit}</span>
                                  </button>
                                  {expanded[bkey] && (
                                    <ul className="mt-1 space-y-0.5 text-xs">
                                      {b.containers.map((c) => (
                                        <li key={c.id} className="flex items-center gap-2 py-1 pl-4 border-l border-border">
                                          <span className="container-code text-primary">{c.code}</span>
                                          <span className="text-muted-foreground truncate">{c.description}</span>
                                          <span className="ml-auto font-mono font-semibold">{c.qty}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
